import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import cccAuthApi from '../api/cccAuthApi';

function readStoredSession(): {
  token: string | null;
  user: Record<string, unknown> | null;
} {
  if (typeof window === 'undefined') {
    return { token: null, user: null };
  }
  let token: string | null = null;
  let user: Record<string, unknown> | null = null;
  try {
    token =
      localStorage.getItem('jwt_token') || localStorage.getItem('access_token');
    const raw = localStorage.getItem('user');
    if (raw) {
      const parsed = JSON.parse(raw);
      user = parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : null;
    }
  } catch {
    token = null;
    user = null;
  }
  return { token, user };
}

type LoginResult = {
  success: boolean;
  challenge?: 'NEW_PASSWORD_REQUIRED';
  session?: string;
  message?: string;
};

export interface CccAuthValue {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: Record<string, unknown> | null;
  idToken: string | null;
  accessToken: string | null;
  login: (email: string, password: string) => Promise<LoginResult>;
  setNewPassword: (
    session: string,
    newPassword: string,
    email?: string
  ) => Promise<{ success: boolean; message?: string }>;
  refreshUser: () => Promise<void>;
  logout: () => void;
  getIdToken: () => string | null;
  getAccessToken: () => string | null;
}

const CccAuthContext = createContext<CccAuthValue | undefined>(undefined);

export const CCC_AUTH_FALLBACK: CccAuthValue = {
  isAuthenticated: false,
  isLoading: false,
  user: null,
  idToken: null,
  accessToken: null,
  login: async () => ({
    success: false,
    message: 'Autenticação indisponível. Recarregue a página.',
  }),
  setNewPassword: async () => ({
    success: false,
    message: 'Autenticação indisponível.',
  }),
  refreshUser: async () => {},
  logout: () => {},
  getIdToken: () => null,
  getAccessToken: () => null,
};

/** @deprecated Use CccAuthValue — alias for WondertradeLogin / @crm imports */
export type CognitoAuthValue = CccAuthValue;

export function CccAuthProvider({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<Record<string, unknown> | null>(null);
  const [idToken, setIdToken] = useState<string | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);

  useEffect(() => {
    const { token, user: storedUser } = readStoredSession();
    if (token) {
      setIsAuthenticated(true);
      setAccessToken(token);
      setIdToken(token);
      if (storedUser) setUser(storedUser);
    }
    setIsLoading(false);
  }, []);

  const login = useCallback(async (email: string, password: string): Promise<LoginResult> => {
    setIsLoading(true);
    try {
      const resp = await cccAuthApi.login(email, password);
      if (resp?.success) {
        const token =
          resp.jwt_token ||
          resp.token ||
          localStorage.getItem('jwt_token') ||
          localStorage.getItem('access_token');
        setIsAuthenticated(true);
        setUser((resp.user as Record<string, unknown>) || {});
        if (token) {
          setAccessToken(token);
          setIdToken(token);
        }
        return { success: true };
      }
      if (resp?.challenge && resp?.session) {
        return {
          success: false,
          challenge: 'NEW_PASSWORD_REQUIRED',
          session: resp.session,
          message: resp.message,
        };
      }
      return {
        success: false,
        message: resp?.message || 'Credenciais inválidas. Tente novamente.',
      };
    } catch (err: unknown) {
      const msg = (err as { message?: string })?.message;
      return { success: false, message: msg || 'Erro no login. Tente novamente.' };
    } finally {
      setIsLoading(false);
    }
  }, []);

  const setNewPassword = useCallback(
    async (
      session: string,
      newPassword: string,
      email?: string
    ): Promise<{ success: boolean; message?: string }> => {
      setIsLoading(true);
      try {
        if (!email) return { success: false, message: 'Email em falta' };
        const resp = await cccAuthApi.respondToAuthChallenge(email, session, newPassword);
        if (resp?.success) {
          const token =
            resp.jwt_token ||
            resp.token ||
            localStorage.getItem('jwt_token') ||
            localStorage.getItem('access_token');
          setIsAuthenticated(true);
          setUser((resp.user as Record<string, unknown>) || {});
          if (token) {
            setAccessToken(token);
            setIdToken(token);
          }
          return { success: true };
        }
        return { success: false, message: resp?.message };
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  const refreshUser = useCallback(async () => {
    try {
      const me = await cccAuthApi.getMe();
      if (me && typeof me === 'object') {
        setUser(me);
        try {
          const raw = JSON.stringify(me);
          localStorage.setItem('user', raw);
          localStorage.setItem('cognito_user', raw);
          if (me.tenant != null) localStorage.setItem('user_tenant', String(me.tenant));
        } catch {
          /* storage may be blocked */
        }
      }
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    if (!isLoading && isAuthenticated && !user) {
      refreshUser();
    }
  }, [isLoading, isAuthenticated, user, refreshUser]);

  const logout = useCallback(() => {
    cccAuthApi.logout();
    setIsAuthenticated(false);
    setUser(null);
    setIdToken(null);
    setAccessToken(null);
  }, []);

  const value = useMemo<CccAuthValue>(
    () => ({
      isAuthenticated,
      isLoading,
      user,
      idToken,
      accessToken,
      login,
      setNewPassword,
      refreshUser,
      logout,
      getIdToken: () => idToken,
      getAccessToken: () => accessToken,
    }),
    [isAuthenticated, isLoading, user, idToken, accessToken, login, setNewPassword, refreshUser, logout]
  );

  return <CccAuthContext.Provider value={value}>{children}</CccAuthContext.Provider>;
}

/** Drop-in for @crm/contexts/CognitoAuthContext in CCC app. */
export function useCognitoAuth(): CccAuthValue {
  return useContext(CccAuthContext) ?? CCC_AUTH_FALLBACK;
}

export function useCccAuth(): CccAuthValue {
  return useContext(CccAuthContext) ?? CCC_AUTH_FALLBACK;
}

/** Safe auth read — never undefined; flags when provider is missing. */
export function useSafeCccAuth(): { auth: CccAuthValue; hasProvider: boolean } {
  const ctx = useContext(CccAuthContext);
  return {
    auth: ctx ?? CCC_AUTH_FALLBACK,
    hasProvider: ctx !== undefined,
  };
}
